/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
export const WALLET_LINK_PROTOCOL_VERSION = 2;
export const WALLET_LINK_DAPP_ID = "horizon";
export const WALLET_LINK_ENCRYPTION_ALGORITHM = "ECDH-P256/HKDF-SHA256/AES-256-GCM";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function toArrayBuffer(value) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function canonicalValue(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalValue).join(",")}]`;
  if (value !== null && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${canonicalValue(value[key])}`)
      .join(",")}}`;
  }
  const encoded = JSON.stringify(value);
  if (encoded === undefined) throw new TypeError("Unsupported canonical Wallet Link value.");
  return encoded;
}

export function canonicalJson(value) {
  return canonicalValue(value);
}

export function base64UrlEncode(value) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function base64UrlDecode(value) {
  const text = String(value || "");
  if (!text || !/^[A-Za-z0-9_-]+$/.test(text)) throw new Error("Expected base64url Wallet Link data.");
  const padded = text.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (text.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function randomBase64Url(bytes = 32) {
  const value = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(value);
  return base64UrlEncode(value);
}

export async function sha256Base64Url(value) {
  const bytes = typeof value === "string" ? textEncoder.encode(value) : value;
  return base64UrlEncode(await globalThis.crypto.subtle.digest("SHA-256", toArrayBuffer(bytes)));
}

function publicJwk(value) {
  if (value?.kty !== "EC" || value?.crv !== "P-256" || !value.x || !value.y || value.d) {
    throw new Error("Wallet Link requires a public P-256 JWK.");
  }
  return { crv: "P-256", ext: true, kty: "EC", x: value.x, y: value.y };
}

export function encodeWalletLinkPublicKey(value) {
  return base64UrlEncode(textEncoder.encode(canonicalJson(publicJwk(value))));
}

export function decodeWalletLinkPublicKey(value) {
  try {
    return publicJwk(JSON.parse(textDecoder.decode(base64UrlDecode(value))));
  } catch (error) {
    if (error instanceof Error && error.message === "Wallet Link requires a public P-256 JWK.") throw error;
    throw new Error("Wallet Link participant key is not valid base64url JSON.");
  }
}

export async function walletLinkPublicKeyFingerprint(value) {
  return sha256Base64Url(canonicalJson(decodeWalletLinkPublicKey(value)));
}

export async function createWalletLinkBrowserParticipant() {
  const signing = await globalThis.crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign", "verify"]
  );
  const transport = await globalThis.crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"]
  );
  if (signing.privateKey.extractable || transport.privateKey.extractable) {
    throw new Error("Wallet Link refused extractable browser private keys.");
  }
  const signingPublicKey = encodeWalletLinkPublicKey(await globalThis.crypto.subtle.exportKey("jwk", signing.publicKey));
  const transportPublicKey = encodeWalletLinkPublicKey(await globalThis.crypto.subtle.exportKey("jwk", transport.publicKey));
  if (await walletLinkPublicKeyFingerprint(signingPublicKey) === await walletLinkPublicKeyFingerprint(transportPublicKey)) {
    throw new Error("Wallet Link signing and transport keys must be distinct.");
  }
  return {
    signingPrivateKey: signing.privateKey,
    signingPublicKey,
    transportPrivateKey: transport.privateKey,
    transportPublicKey
  };
}

export async function createWalletLinkProofHeaders({
  participant,
  origin,
  method,
  path,
  bodyText = "",
  sessionId = null,
  issuedAt = new Date().toISOString(),
  nonce = randomBase64Url(24)
}) {
  const payload = {
    protocolVersion: WALLET_LINK_PROTOCOL_VERSION,
    purpose: "synergy-wallet-link-browser-request",
    dappId: WALLET_LINK_DAPP_ID,
    origin,
    sessionId,
    httpMethod: method.toUpperCase(),
    path,
    bodyHash: await sha256Base64Url(bodyText),
    nonce,
    issuedAt
  };
  const signature = new Uint8Array(await globalThis.crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    participant.signingPrivateKey,
    textEncoder.encode(canonicalJson(payload))
  ));
  if (signature.length !== 64) throw new Error("Wallet Link browser proof did not produce a raw P-256 signature.");
  const headers = new Headers({
    Accept: "application/json",
    "X-Synergy-Wallet-Protocol": String(WALLET_LINK_PROTOCOL_VERSION),
    "X-Synergy-DApp-Id": WALLET_LINK_DAPP_ID,
    "X-Synergy-DApp-Origin": origin,
    "X-Synergy-Browser-Timestamp": issuedAt,
    "X-Synergy-Browser-Nonce": nonce,
    "X-Synergy-Browser-Proof": base64UrlEncode(signature)
  });
  if (bodyText) headers.set("Content-Type", "application/json");
  return headers;
}

async function deriveTransportKey({ privateKey, peerPublicKey, aadBytes }) {
  if (privateKey.extractable || privateKey.algorithm?.name !== "ECDH") {
    throw new Error("Wallet Link requires a nonextractable ECDH P-256 transport key.");
  }
  const peer = await globalThis.crypto.subtle.importKey(
    "jwk",
    decodeWalletLinkPublicKey(peerPublicKey),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );
  const sharedX = await globalThis.crypto.subtle.deriveBits({ name: "ECDH", public: peer }, privateKey, 256);
  const hkdfKey = await globalThis.crypto.subtle.importKey("raw", sharedX, "HKDF", false, ["deriveKey"]);
  return globalThis.crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: await globalThis.crypto.subtle.digest("SHA-256", toArrayBuffer(aadBytes)),
      info: textEncoder.encode("synergy-wallet-link-v2")
    },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptWalletLinkEnvelope({
  participant,
  plaintext,
  aad,
  peerPublicKey,
  senderKeyFingerprint,
  recipientKeyFingerprint
}) {
  const aadBytes = textEncoder.encode(canonicalJson(aad));
  const nonce = new Uint8Array(12);
  globalThis.crypto.getRandomValues(nonce);
  const key = await deriveTransportKey({ privateKey: participant.transportPrivateKey, peerPublicKey, aadBytes });
  const ciphertext = await globalThis.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(nonce), additionalData: toArrayBuffer(aadBytes), tagLength: 128 },
    key,
    toArrayBuffer(textEncoder.encode(canonicalJson(plaintext)))
  );
  return {
    version: 1,
    algorithm: WALLET_LINK_ENCRYPTION_ALGORITHM,
    senderKeyFingerprint,
    recipientKeyFingerprint,
    aadHash: await sha256Base64Url(aadBytes),
    nonce: base64UrlEncode(nonce),
    ciphertext: base64UrlEncode(ciphertext)
  };
}

export async function decryptWalletLinkEnvelope({
  participant,
  envelope,
  aad,
  peerPublicKey,
  senderKeyFingerprint,
  recipientKeyFingerprint
}) {
  if (
    Number(envelope?.version) !== 1 ||
    envelope?.algorithm !== WALLET_LINK_ENCRYPTION_ALGORITHM ||
    envelope?.senderKeyFingerprint !== senderKeyFingerprint ||
    envelope?.recipientKeyFingerprint !== recipientKeyFingerprint
  ) throw new Error("Wallet Link encrypted-envelope participant binding is invalid.");
  const aadBytes = textEncoder.encode(canonicalJson(aad));
  if (envelope.aadHash !== await sha256Base64Url(aadBytes)) {
    throw new Error("Wallet Link encrypted-envelope AAD binding is invalid.");
  }
  const nonce = base64UrlDecode(envelope.nonce);
  if (nonce.length !== 12) throw new Error("Wallet Link encrypted-envelope nonce is invalid.");
  const key = await deriveTransportKey({ privateKey: participant.transportPrivateKey, peerPublicKey, aadBytes });
  let plaintext;
  try {
    plaintext = await globalThis.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(nonce), additionalData: toArrayBuffer(aadBytes), tagLength: 128 },
      key,
      toArrayBuffer(base64UrlDecode(envelope.ciphertext))
    );
  } catch {
    throw new Error("Wallet Link encrypted envelope could not be authenticated.");
  }
  try {
    return JSON.parse(textDecoder.decode(plaintext));
  } catch {
    throw new Error("Wallet Link encrypted envelope did not contain JSON.");
  }
}

function exactText(value, expected, label) {
  if (value !== expected) throw new Error(`Wallet Web Hub attestation ${label} binding is invalid.`);
}

export async function verifyWalletLinkAttestation({ attestation, keySet, proposal, proposalBodyText, now = Date.now() }) {
  if (keySet?.issuer !== "synergy-wallet-web-hub" || !Array.isArray(keySet?.keys)) {
    throw new Error("Wallet Web Hub returned an invalid attestation key set.");
  }
  const keyRecord = keySet.keys.find((entry) => entry?.kid === attestation?.keyId);
  if (!keyRecord || keyRecord.alg !== "ES256" || keyRecord.use !== "sig") {
    throw new Error("Wallet Web Hub attestation signing key is not trusted.");
  }
  if (attestation?.algorithm !== "ECDSA-P256-SHA256") {
    throw new Error("Wallet Web Hub attestation algorithm is unsupported.");
  }
  const payload = attestation?.payload;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("Wallet Web Hub returned a malformed dApp attestation.");
  }
  exactText(payload.type, "synergy-wallet-link-dapp-attestation", "type");
  if (Number(payload.version) !== WALLET_LINK_PROTOCOL_VERSION) throw new Error("Wallet Web Hub attestation version is invalid.");
  exactText(payload.issuer, "synergy-wallet-web-hub", "issuer");
  exactText(payload.audience, "synergy-wallet-link-service", "audience");
  for (const key of ["dappId", "origin", "redirectUri", "browserPublicKey", "browserTransportPublicKey", "codeChallenge"]) {
    exactText(payload[key], String(proposal[key] || ""), key);
  }
  exactText(payload.codeChallengeMethod, "S256", "PKCE method");
  exactText(payload.stateHash, await sha256Base64Url(String(proposal.state || "")), "state");
  exactText(payload.proposalBodyHash, await sha256Base64Url(proposalBodyText), "proposal body");
  if (!/^[A-Za-z0-9_-]{22,180}$/.test(String(payload.jti || ""))) {
    throw new Error("Wallet Web Hub attestation identifier is invalid.");
  }
  const issuedAt = Date.parse(String(payload.issuedAt || ""));
  const expiresAt = Date.parse(String(payload.expiresAt || ""));
  if (
    !Number.isFinite(issuedAt) ||
    !Number.isFinite(expiresAt) ||
    Math.abs(now - issuedAt) > 90_000 ||
    expiresAt <= now ||
    expiresAt > issuedAt + 90_000
  ) throw new Error("Wallet Web Hub attestation has expired or has an invalid lifetime.");
  const signature = base64UrlDecode(String(attestation.signature || ""));
  if (signature.length !== 64) throw new Error("Wallet Web Hub attestation signature is invalid.");
  const key = await globalThis.crypto.subtle.importKey(
    "jwk",
    publicJwk(keyRecord.jwk),
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["verify"]
  );
  const verified = await globalThis.crypto.subtle.verify(
    { name: "ECDSA", hash: "SHA-256" },
    key,
    toArrayBuffer(signature),
    toArrayBuffer(textEncoder.encode(canonicalJson(payload)))
  );
  if (!verified) throw new Error("Wallet Web Hub attestation signature is invalid.");
}
